import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { usePublishScreenData } from "../utils/aiScreenContext";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import ActionButton from "../components/ActionButton";

// Website / URL monitoring (Monitoring & Alerts). Add HTTP(S) endpoints to
// watch for uptime, expected response and TLS-certificate expiry.

type Url = {
  id: number;
  customer_id?: number | null;
  customer_name?: string | null;
  name: string;
  url: string;
  method: string;
  expected_status: number;
  keyword?: string | null;
  timeout_seconds: number;
  follow_redirects: boolean;
  verify_tls: boolean;
  check_interval_seconds: number;
  failures_before_alert: number;
  cert_warn_days: number;
  enabled: boolean;
  last_status: string;
  last_status_code?: number | null;
  last_response_ms?: number | null;
  last_checked_at?: string | null;
  last_error?: string | null;
  cert_not_after?: string | null;
};

const BLANK = {
  name: "",
  url: "https://",
  customer_id: "" as string | number,
  method: "GET",
  expected_status: 200,
  keyword: "",
  timeout_seconds: 15,
  follow_redirects: true,
  verify_tls: true,
  check_interval_seconds: 300,
  failures_before_alert: 2,
  cert_warn_days: 14,
  enabled: true,
};

function statusBadge(u: Url) {
  const s = u.last_status;
  if (s === "up") return { label: "up", cls: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" };
  if (s === "down") return { label: "down", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" };
  return { label: "pending", cls: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300" };
}

function certDays(u: Url): number | null {
  if (!u.cert_not_after) return null;
  return Math.floor((new Date(u.cert_not_after).getTime() - Date.now()) / 86400000);
}

export default function UrlMonitoring() {
  const [urls, setUrls] = useState<Url[]>([]);
  const [customers, setCustomers] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState<any | null>(null); // form state or null
  const urlSort = useSort("name", "asc");

  // Publish the visible rows so the global AI Assist can answer
  // "what's on my screen". Projected explicitly - credential and
  // machine-output fields must not reach a prompt.
  usePublishScreenData(
    "Website monitors",
    useMemo(
      () =>
        urls.map((u) => ({
          name: u.name,
          url: u.url,
          customer: u.customer_name ?? null,
          enabled: (u as any).enabled ?? null,
          last_status: (u as any).last_status ?? null,
          last_http_code: (u as any).last_http_code ?? null,
          last_checked_at: (u as any).last_checked_at ?? null,
          cert_expires_at: (u as any).cert_not_after ?? null,
        })),
      [urls],
    ),
    undefined,
  );

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/api/v1/monitored-urls")
      .then((r) => setUrls(r.data || []))
      .catch((e) => setErr(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    load();
    api
      .get("/api/v1/customers")
      .then((r) => setCustomers((r.data || []).map((c: any) => ({ id: c.id, name: c.name }))))
      .catch(() => {});
  }, [load]);

  const save = async () => {
    setErr("");
    try {
      const body = {
        ...editing,
        customer_id: editing.customer_id === "" ? null : Number(editing.customer_id),
        keyword: editing.keyword || null,
      };
      if (editing.id) await api.put(`/api/v1/monitored-urls/${editing.id}`, body);
      else await api.post("/api/v1/monitored-urls", body);
      setEditing(null);
      load();
    } catch (e: any) {
      setErr(e.response?.data?.detail || "Failed to save");
    }
  };

  const remove = async (u: Url) => {
    if (!window.confirm(`Delete monitor "${u.name}"?`)) return;
    await api.delete(`/api/v1/monitored-urls/${u.id}`);
    load();
  };

  const checkNow = async (u: Url) => {
    try {
      await api.post(`/api/v1/monitored-urls/${u.id}/check`);
      load();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    }
  };

  const startEdit = (u?: Url) =>
    setEditing(u ? { ...u, customer_id: u.customer_id ?? "", keyword: u.keyword ?? "" } : { ...BLANK });

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            Websites / URLs
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Monitor HTTP(S) endpoints for uptime, expected response and TLS-certificate expiry.
          </p>
        </div>
        <button
          onClick={() => startEdit()}
          className="bg-brand text-white text-sm px-4 py-2 rounded-lg hover:opacity-90"
        >
          + Add website
        </button>
      </div>

      {err && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 px-4 py-2 rounded-lg text-sm">
          {err}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500 dark:text-gray-400 text-xs uppercase">
            <tr>
              <SortableTh label="Status" sortKey="last_status" sort={urlSort.sort} onSort={urlSort.toggle} />
              <SortableTh label="Name" sortKey="name" sort={urlSort.sort} onSort={urlSort.toggle} />
              <SortableTh label="URL" sortKey="url" sort={urlSort.sort} onSort={urlSort.toggle} hide="hidden lg:table-cell" />
              <SortableTh label="Response" sortKey="last_response_ms" sort={urlSort.sort} onSort={urlSort.toggle} hide="hidden md:table-cell" />
              <SortableTh label="Cert expiry" sortKey="cert" sort={urlSort.sort} onSort={urlSort.toggle} hide="hidden lg:table-cell" />
              <SortableTh label="Customer" sortKey="customer" sort={urlSort.sort} onSort={urlSort.toggle} hide="hidden md:table-cell" />
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                  Loading…
                </td>
              </tr>
            ) : urls.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                  No websites monitored yet. Click “Add website” to start.
                </td>
              </tr>
            ) : (
              sortRows(urls, urlSort.sort, (u, key) => {
                switch (key) {
                  case "last_status": return (u.last_status || "").toLowerCase();
                  case "url": return (u.url || "").toLowerCase();
                  case "last_response_ms": return u.last_response_ms ?? 0;
                  case "cert": return u.cert_not_after ? Date.parse(u.cert_not_after) : 0;
                  case "customer": return (u.customer_name || "").toLowerCase();
                  default: return (u.name || "").toLowerCase();
                }
              }).map((u) => {
                const b = statusBadge(u);
                const cd = certDays(u);
                return (
                  <tr key={u.id} className="text-gray-800 dark:text-gray-100">
                    <td className="px-4 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${b.cls}`}>
                        {b.label}
                      </span>
                    </td>
                    <td className="px-4 py-2 font-medium">{u.name}</td>
                    <td className="px-4 py-2 max-w-xs truncate hidden lg:table-cell">
                      <a href={u.url} target="_blank" rel="noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">
                        {u.url}
                      </a>
                      {u.last_error && u.last_status === "down" && (
                        <div className="text-xs text-red-500 truncate">{u.last_error}</div>
                      )}
                    </td>
                    <td className="px-4 py-2 hidden md:table-cell">
                      {u.last_response_ms != null ? `${u.last_response_ms} ms` : "—"}
                      {u.last_status_code ? (
                        <span className="text-xs text-gray-400 ml-1">({u.last_status_code})</span>
                      ) : null}
                    </td>
                    <td className="px-4 py-2 hidden lg:table-cell">
                      {cd == null ? (
                        "—"
                      ) : (
                        <span className={cd < 14 ? "text-red-600 font-medium" : ""}>
                          {cd < 0 ? "expired" : `${cd}d`}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 hidden md:table-cell">{u.customer_name || "—"}</td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <button onClick={() => checkNow(u)} className="text-xs text-blue-600 dark:text-blue-400 hover:underline mr-3">
                        Check now
                      </button>
                      <button onClick={() => startEdit(u)} className="text-xs text-gray-500 dark:text-gray-400 hover:underline mr-3">
                        Edit
                      </button>
                      <button onClick={() => remove(u)} className="text-xs text-red-500 hover:underline">
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
              {editing.id ? "Edit website" : "Add website"}
            </h2>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Field label="Name" full>
                <input className={inp} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </Field>
              <Field label="URL" full>
                <input className={inp} value={editing.url} onChange={(e) => setEditing({ ...editing, url: e.target.value })} />
              </Field>
              <Field label="Customer">
                <select
                  className={inp}
                  value={editing.customer_id}
                  onChange={(e) => setEditing({ ...editing, customer_id: e.target.value })}
                >
                  <option value="">All / none</option>
                  {customers.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Method">
                <select className={inp} value={editing.method} onChange={(e) => setEditing({ ...editing, method: e.target.value })}>
                  <option>GET</option>
                  <option>HEAD</option>
                </select>
              </Field>
              <Field label="Expected status">
                <input type="number" className={inp} value={editing.expected_status} onChange={(e) => setEditing({ ...editing, expected_status: Number(e.target.value) })} />
              </Field>
              <Field label="Body keyword (optional)">
                <input className={inp} value={editing.keyword} onChange={(e) => setEditing({ ...editing, keyword: e.target.value })} />
              </Field>
              <Field label="Check interval (sec)">
                <input type="number" className={inp} value={editing.check_interval_seconds} onChange={(e) => setEditing({ ...editing, check_interval_seconds: Number(e.target.value) })} />
              </Field>
              <Field label="Timeout (sec)">
                <input type="number" className={inp} value={editing.timeout_seconds} onChange={(e) => setEditing({ ...editing, timeout_seconds: Number(e.target.value) })} />
              </Field>
              <Field label="Failures before alert">
                <input type="number" className={inp} value={editing.failures_before_alert} onChange={(e) => setEditing({ ...editing, failures_before_alert: Number(e.target.value) })} />
              </Field>
              <Field label="Cert warning (days)">
                <input type="number" className={inp} value={editing.cert_warn_days} onChange={(e) => setEditing({ ...editing, cert_warn_days: Number(e.target.value) })} />
              </Field>
              <label className="flex items-center gap-2 mt-1">
                <input type="checkbox" checked={editing.follow_redirects} onChange={(e) => setEditing({ ...editing, follow_redirects: e.target.checked })} />
                <span>Follow redirects</span>
              </label>
              <label className="flex items-center gap-2 mt-1">
                <input type="checkbox" checked={editing.verify_tls} onChange={(e) => setEditing({ ...editing, verify_tls: e.target.checked })} />
                <span>Verify TLS cert</span>
              </label>
              <label className="flex items-center gap-2 mt-1">
                <input type="checkbox" checked={editing.enabled} onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} />
                <span>Enabled</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setEditing(null)} className="text-sm px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600">
                Cancel
              </button>
              <ActionButton onAction={() => save()} variant="raw" className="text-sm px-4 py-2 rounded-lg bg-brand text-white" pendingLabel="Saving…">
                Save
              </ActionButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inp =
  "w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 dark:bg-gray-900 dark:text-gray-100 text-sm";

function Field({ label, children, full }: { label: string; children: React.ReactNode; full?: boolean }) {
  return (
    <label className={full ? "col-span-2" : ""}>
      <span className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">{label}</span>
      {children}
    </label>
  );
}
